import { memo } from 'react';
import { getSeatDisplayText } from 'src/components/common/SeatingInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  FormatOption,
  FormatOption_Seating,
} from 'src/modals/EditTableColumns';
import { SeatSaverIcon } from 'src/svgs';
import { ClockTrashIcon } from 'src/svgs/ClockTrashIcon';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { Feature, ListingNote } from 'src/WebApiController';

import { BaseCell } from '../BaseCell';
import { StackedCell } from '../StackedCell';
import * as styles from './SeatingCell.css';

export interface SeatingCellProps {
  section: string;
  row: string | null;
  seatFr: string | null;
  seatTo: string | null;
  isSeatSaver?: boolean;
  isWasted?: boolean;
  hasMarketplaceProblem?: boolean;
  isLoading?: boolean;
  userDefinedPrecision?: FormatOption;
  seatTraits?: ListingNote[] | null;
}

const SeatingCellComponent = ({
  section,
  row,
  seatFr,
  seatTo,
  isSeatSaver,
  isWasted,
  hasMarketplaceProblem,
  isLoading,
  userDefinedPrecision,
  seatTraits,
}: SeatingCellProps) => {
  const hasSeatTraitsColumnFeature = useUserHasFeature(
    Feature.SeatTraitsColumn
  );
  if (isLoading) {
    return <TableShimmeringDiv height="40px" />;
  }
  const isSectionOnly =
    userDefinedPrecision === FormatOption_Seating.Seating_SectionOnly;

  const displayRow = isSectionOnly ? null : row;
  const displaySeatFr = isSectionOnly ? null : seatFr;
  const displaySeatTo = isSectionOnly ? null : seatTo;

  const seatDisplay = getSeatDisplayText({
    seatFr: displaySeatFr,
    seatTo: displaySeatTo,
  });

  // Build icons
  const icons = (
    <Stack gap="s" className={styles.icons}>
      {isWasted && <ClockTrashIcon stroke={IconsFill.currentColor} />}
      {isSeatSaver && (
        <SeatSaverIcon fill={vars.color.textWarning} size={vars.iconSize.m} />
      )}
      {hasMarketplaceProblem && (
        <TooltipPopover
          variant="link"
          triggerContent={
            <WarningOutlineIcon fill={IconsFill.textWarning} withHoverEffect />
          }
        >
          <WarningOutlineIcon
            fill={IconsFill.textWarning}
            size={vars.iconSize.m}
          />
        </TooltipPopover>
      )}
    </Stack>
  );

  // Build the primary display (section)
  const primaryContent = (
    <Stack justifyContent="spaceBetween" alignItems="center" width="full">
      <Stack gap="s" alignItems="center">
        <span>{section}</span>
        {displayRow && (
          <>
            <span className={styles.primaryLineSoftText}>Row</span>
            <span>{displayRow}</span>
          </>
        )}
        {seatDisplay && (
          <>
            <span className={styles.primaryLineSoftText}>Seats</span>
            <span>{seatDisplay}</span>
          </>
        )}
      </Stack>
      {icons}
    </Stack>
  );
  // Build the secondary display (row, seats, and traits)
  const secondaryContent =
    !isSectionOnly && hasSeatTraitsColumnFeature && seatTraits ? (
      <Stack gap="s" alignItems="center" className={styles.secondaryText}>
        <ListingNotesDisplay seatTraits={seatTraits} />
      </Stack>
    ) : null;

  if (!secondaryContent) {
    // Single line display
    return <BaseCell showPadding>{primaryContent}</BaseCell>;
  }

  // Multi-line display using stacked layout
  return (
    <StackedCell
      primary={primaryContent}
      secondary={secondaryContent}
      context={undefined}
    />
  );
};

SeatingCellComponent.displayName = 'SeatingCell';

export const SeatingCell = memo(SeatingCellComponent);
